"use client"

import { useEffect, useState } from "react"
import { createClient } from "@/lib/supabase/client"
import type { User } from "@supabase/supabase-js"

// Demo mode - bypasses authentication for demonstration purposes
// Enabled by default - set NEXT_PUBLIC_DEMO_MODE=false to disable
const DEMO_MODE = process.env.NEXT_PUBLIC_DEMO_MODE !== "false"

// Mock user for demo mode
const DEMO_USER: User = {
  id: "demo-user-id",
  email: "demo@logosiq.ai",
  app_metadata: {},
  user_metadata: { 
    tenant_id: "demo-tenant-id", 
    is_demo: true,
    full_name: "Demo User"
  },
  aud: "authenticated",
  created_at: new Date().toISOString(),
} as User

export interface AuthState {
  user: User | null
  loading: boolean
}

export type { User }

function logAuthProof(label: string, session: any) {
  const isStaging =
    typeof window !== "undefined" &&
    (window.location.hostname === "dev.bizziq.ai" || process.env.NEXT_PUBLIC_VERCEL_ENV === "preview")

  if (!isStaging) return

  const hasSession = !!session?.user
  const sessionUserId = session?.user?.id
  const expiresAt = session?.expires_at

  console.log(`[Auth] ${label}:`, {
    hasSession,
    sessionUserId: sessionUserId ? `${sessionUserId.substring(0, 8)}...` : null,
    expiresAt: expiresAt ? new Date(expiresAt * 1000).toISOString() : null,
  })
}

export function useAuth() {
  const [user, setUser] = useState<User | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    // Demo mode - return mock user immediately
    if (DEMO_MODE) {
      console.log("[Auth] Demo mode enabled - using mock user")
      setUser(DEMO_USER)
      setLoading(false)
      return
    }

    const checkDemoSession = () => {
      if (typeof document === "undefined") return null

      const cookies = document.cookie.split(";").reduce(
        (acc, cookie) => {
          const [key, value] = cookie.trim().split("=")
          acc[key] = value
          return acc
        },
        {} as Record<string, string>,
      )

      const demoSession = cookies["demo-session"]
      if (demoSession) {
        try {
          const session = JSON.parse(decodeURIComponent(demoSession))
          if (session.exp && session.exp > Date.now()) {
            console.log("[Auth] Demo session detected, creating mock user")
            return {
              id: session.userId,
              email: session.email,
              app_metadata: {},
              user_metadata: { tenant_id: session.tenantId, is_demo: true },
              aud: "authenticated",
              created_at: new Date().toISOString(),
            } as User
          }
        } catch (e) {
          console.error("[Auth] Failed to parse demo session:", e)
        }
      }
      return null
    }

    const demoUser = checkDemoSession()
    if (demoUser) {
      setUser(demoUser)
      setLoading(false)
      return
    }

    const supabase = createClient()

    supabase.auth.getSession().then(({ data: { session }, error }) => {
      logAuthProof("Client state on mount", session)
      setUser(session?.user ?? null)
      setLoading(false)

      if (error) {
        console.error("[Auth] getSession error:", error.message)
      }
    })

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event, session) => {
      const isStaging =
        typeof window !== "undefined" &&
        (window.location.hostname === "dev.bizziq.ai" || process.env.NEXT_PUBLIC_VERCEL_ENV === "preview")

      if (isStaging) {
        if (event === "SIGNED_IN") {
          console.log("[Auth] ✅ SIGNED_IN event detected")
          logAuthProof("After SIGNED_IN", session)
        } else if (event === "SIGNED_OUT") {
          console.log("[Auth] ⚠️ SIGNED_OUT event detected")
        } else if (event === "TOKEN_REFRESHED") {
          console.log("[Auth] 🔄 TOKEN_REFRESHED event detected")
        } else if (event === "INITIAL_SESSION") {
          console.log("[Auth] 📋 INITIAL_SESSION event detected")
          logAuthProof("INITIAL_SESSION", session)
        } else {
          console.log(`[Auth] Event: ${event}`)
        }
      }

      setUser(session?.user ?? null)
    })

    return () => subscription.unsubscribe()
  }, [])

  const signIn = async (email: string, password: string) => {
    const supabase = createClient()
    return supabase.auth.signInWithPassword({ email, password })
  }

  const signUp = async (email: string, password: string) => {
    const supabase = createClient()
    const redirectTo =
      typeof window !== "undefined"
        ? `${window.location.origin}/auth/callback`
        : process.env.NEXT_PUBLIC_SUPABASE_REDIRECT_URL || process.env.NEXT_PUBLIC_SITE_URL || "/auth/callback"

    return supabase.auth.signUp({
      email,
      password,
      options: {
        emailRedirectTo: redirectTo,
      },
    })
  }

  const signOut = async () => {
    const supabase = createClient()
    return supabase.auth.signOut()
  }

  return { user, loading, signIn, signUp, signOut }
}

export function useRequireAuth() {
  const { user, loading } = useAuth()
  return { isAuthenticated: !!user && !loading, user, loading }
}
